import { useRef, useState, useEffect } from "react";
import Spinner from "../common/Spinner";
import { useNavigate } from "react-router-dom";
import roscasordersService from '../../services/roscaorders.service'
import { RoscaOrder } from "../../models/RoscaOrder";
import './RoscaOrders.css';
import CustomerPicker from '../customers/CustomerPicker';
import { displayStringToDate, dateToDisplayString, getRoscaSeasonYear, DATE_OPTIONS, TIME_CONFIG } from '../../helpers/roscas';
import { formatPhoneForDisplay } from '../../helpers/phone';
import PrintContainer from '../PrintContainer';
import ThermalReceipt, { ThermalReceiptRow, ThermalReceiptSection } from '../receipts/ThermalReceipt';
import storeLogo from '../../images/logo-los-portales.png';
import { getStoreLabel } from '../../constants/stores';
import { logBeginOrder, logSubmitOrder, logOrderSuccess, logOrderError, OrderTypes } from '../../services/analytics.service';

export default function RoscaOrderForm() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [submittedOrder, setSubmittedOrder] = useState(null);
    const form = useRef();
    const receiptRef = useRef();

    // Form state
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [amount, setAmount] = useState('1');
    const [paid, setPaid] = useState('');
    const [cashier, setCashier] = useState('');

    // Customer state
    const [customerId, setCustomerId] = useState(null);

    // Validation state
    const [dateValid, setDateValid] = useState(true);
    const [timeValid, setTimeValid] = useState(true);
    const [firstNameValid, setFirstNameValid] = useState(true);
    const [lastNameValid, setLastNameValid] = useState(true);
    const [phoneNumberValid, setPhoneNumberValid] = useState(true);
    const [amountValid, setAmountValid] = useState(true);
    const [paidValid, setPaidValid] = useState(true);
    const [cashierValid, setCashierValid] = useState(true);

    useEffect(() => {
        logBeginOrder(OrderTypes.ROSCA);
    }, []);

    const roscaYear = getRoscaSeasonYear();

    function handleCustomerChange({ firstName: fn, lastName: ln, phoneNumber: pn, customerId: cid }) {
        setFirstName(fn);
        setLastName(ln);
        setPhoneNumber(pn);
        setCustomerId(cid);
        if (fn) setFirstNameValid(true);
        if (ln) setLastNameValid(true);
        if (pn) setPhoneNumberValid(true);
    }

    function validate() {
        let isValid = true;
        if (!date) { setDateValid(false); isValid = false; }
        if (!time) { setTimeValid(false); isValid = false; }
        if (!firstName) { setFirstNameValid(false); isValid = false; }
        if (!lastName) { setLastNameValid(false); isValid = false; }
        if (!phoneNumber || !phoneNumberValid) { setPhoneNumberValid(false); isValid = false; }
        if (!amount || amount <= 0) { setAmountValid(false); isValid = false; }
        if (!paid) { setPaidValid(false); isValid = false; }
        if (!cashier) { setCashierValid(false); isValid = false; }
        if (!isValid) alert("Por favor completa todos los campos o corrige el número de teléfono");
        return isValid;
    }

    async function onFormSubmit(e) {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);

        logSubmitOrder(OrderTypes.ROSCA, {
            hasCustomer: Boolean(customerId),
            hasPrepayment: paid === 'Si'
        });

        try {
            const dateFormat = displayStringToDate(date);
            const orderData = new RoscaOrder(null, dateFormat, time, firstName, lastName, phoneNumber, amount, paid, cashier, null, customerId || null);
            const createdOrder = await roscasordersService.createRoscaOrder(orderData);
            logOrderSuccess(OrderTypes.ROSCA, createdOrder.id);
            setSubmittedOrder(createdOrder);
        } catch (error) {
            logOrderError(OrderTypes.ROSCA, 'submission_failed');
            alert("Error: " + error.message);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="rosca-form-container">
            <div className="rosca-form-header">
                <h1 className="rosca-title">Roscas de Reyes {roscaYear}</h1>
            </div>

            <div className="rosca-form-card">
                <form ref={form} onSubmit={onFormSubmit}>
                    
                    {/* DAY */}
                    <div className="rosca-form-section">
                        <label className="rosca-label">Día de Recogida</label>
                        <div className="rosca-day-selector">
                            {DATE_OPTIONS.map(opt => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    className={`rosca-day-btn ${date === opt.value ? 'selected' : ''} ${!dateValid && !date ? 'invalid' : ''}`}
                                    onClick={() => { setDate(opt.value); setDateValid(true); setTime(''); }}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* TIME */}
                    <div className="rosca-form-section">
                        <label className="rosca-label">Hora</label>
                        <div className={`rosca-time-grid ${!timeValid && !time ? 'invalid' : ''}`}>
                            {(TIME_CONFIG[displayStringToDate(date)] || []).map(t => (
                                <button
                                    key={t}
                                    type="button"
                                    className={`rosca-time-btn ${time === t ? 'selected' : ''} ${!timeValid && !time ? 'invalid' : ''}`}
                                    onClick={() => { setTime(t); setTimeValid(true); }}
                                >
                                    {t}
                                </button>
                            ))}
                        </div>
                        {!date && <p className="rosca-hint">Selecciona un día primero</p>}
                    </div>

                    {/* QUANTITY */}
                    <div className="rosca-form-section">
                        <label className="rosca-label">Cantidad</label>
                        <input
                            type="number"
                            min="1"
                            className={`rosca-input rosca-input-amount ${!amountValid ? 'rosca-input-invalid' : ''}`}
                            value={amount}
                            onChange={(e) => { setAmount(e.target.value); setAmountValid(true); }}
                        />
                    </div>

                    {/* Customer Fields */}
                    <CustomerPicker
                        value={{ firstName, lastName, phoneNumber, customerId }}
                        onChange={handleCustomerChange}
                        validation={{ firstNameValid, lastNameValid, phoneNumberValid }}
                    />

                    {/* PAID */}
                    <div className="rosca-form-section">
                        <label className="rosca-label">Pagado</label>
                        <div className="rosca-toggle-group">
                            <button
                                type="button"
                                className={`rosca-toggle-btn rosca-toggle-yes ${paid === 'Si' ? 'selected' : ''} ${!paidValid && !paid ? 'invalid' : ''}`}
                                onClick={() => { setPaid('Si'); setPaidValid(true); }}
                            >
                                Sí
                            </button>
                            <button
                                type="button"
                                className={`rosca-toggle-btn rosca-toggle-no ${paid === 'No' ? 'selected' : ''} ${!paidValid && !paid ? 'invalid' : ''}`}
                                onClick={() => { setPaid('No'); setPaidValid(true); }}
                            >
                                No
                            </button>
                        </div>
                    </div>

                    {/* CASHIER */}
                    <div className="rosca-form-section">
                        <label className="rosca-label">Cajero</label>
                        <input
                            type="text"
                            className={`rosca-input ${!cashierValid ? 'rosca-input-invalid' : ''}`}
                            value={cashier}
                            onChange={(e) => { setCashier(e.target.value); setCashierValid(true); }}
                        />
                    </div>

                    <div className="rosca-form-actions">
                        {loading ? <Spinner /> : (
                            <button 
                                type="submit" 
                                className="rosca-submit-btn"
                                disabled={!phoneNumberValid || phoneNumber === ''}
                                style={{
                                    opacity: (!phoneNumberValid || phoneNumber === '') ? 0.5 : 1,
                                    cursor: (!phoneNumberValid || phoneNumber === '') ? 'not-allowed' : 'pointer'
                                }}
                            >
                                Crear Orden
                            </button>
                        )}
                    </div>
                </form>
            </div>
            {submittedOrder && (
                <div className="rosca-form-card" style={{ marginTop: '2rem' }}>
                    <h2 style={{ marginBottom: '1rem' }}>Recibo</h2>
                    <PrintContainer
                        contentRef={receiptRef}
                        buttonLabel="Imprimir recibo"
                        receiptData={{
                            title: 'Rosca de Reyes',
                            subtitle: submittedOrder.store_id ? getStoreLabel(submittedOrder.store_id) : '',
                            meta: [
                                { label: 'Día', value: dateToDisplayString(submittedOrder.date) },
                                { label: 'Hora', value: submittedOrder.time },
                                { label: 'Pagado', value: submittedOrder.paid },
                            ],
                            sections: [
                                {
                                    title: 'Contacto',
                                    rows: [
                                        { label: 'Nombre', value: `${submittedOrder.firstName} ${submittedOrder.lastName}` },
                                        { label: 'Teléfono', value: formatPhoneForDisplay(submittedOrder.phoneNumber) },
                                    ],
                                },
                                {
                                    title: 'Orden',
                                    rows: [
                                        { label: 'Cantidad', value: `${submittedOrder.amount} rosca${submittedOrder.amount > 1 ? 's' : ''}` },
                                        { label: 'Cajero', value: submittedOrder.cashier },
                                    ],
                                },
                            ],
                            footerNote: 'Gracias por tu orden',
                        }}
                    >
                        <ThermalReceipt
                            logoSrc={storeLogo}
                            logoAlt="Los Portales"
                            title="Rosca de Reyes"
                            subtitle={submittedOrder.store_id ? getStoreLabel(submittedOrder.store_id) : ''}
                            meta={[
                                { label: 'Día', value: dateToDisplayString(submittedOrder.date) },
                                { label: 'Hora', value: submittedOrder.time },
                                { label: 'Pagado', value: submittedOrder.paid }
                            ]}
                            footerNote="Gracias por tu orden"
                        >
                            <ThermalReceiptSection title="Contacto">
                                <ThermalReceiptRow
                                    label="Nombre"
                                    value={`${submittedOrder.firstName} ${submittedOrder.lastName}`}
                                />
                                <ThermalReceiptRow
                                    label="Teléfono"
                                    value={formatPhoneForDisplay(submittedOrder.phoneNumber)}
                                />
                            </ThermalReceiptSection>
                            <ThermalReceiptSection title="Orden">
                                <ThermalReceiptRow
                                    label="Cantidad"
                                    value={`${submittedOrder.amount} rosca${submittedOrder.amount > 1 ? 's' : ''}`}
                                />
                                <ThermalReceiptRow
                                    label="Cajero"
                                    value={submittedOrder.cashier}
                                />
                            </ThermalReceiptSection>
                        </ThermalReceipt>
                    </PrintContainer>
                    <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem' }}>
                        <button 
                            type="button" 
                            className="rosca-submit-btn"
                            onClick={() => {
                                setSubmittedOrder(null);
                                navigate('/success');
                            }}
                        >
                            Continuar
                        </button>
                        <button
                            type="button"
                            className="rosca-submit-btn"
                            onClick={() => {
                                setSubmittedOrder(null);
                                // Reset form
                                setDate('');
                                setTime('');
                                setFirstName('');
                                setLastName('');
                                setPhoneNumber('');
                                setAmount('1');
                                setPaid('');
                                setCashier('');
                                setCustomerId(null);
                            }}
                            style={{ backgroundColor: '#6c757d' }}
                        >
                            Crear Otra Orden
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
